/**
 * Скачивание треков с любого источника (одиночное и пачкой для плейлистов)
 */

import axios from 'axios';
import { API_URL } from './constants';

const sanitizeFilename = (name) =>
  String(name || '')
    .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 150);

/**
 * Имя файла "Исполнитель - Название.mp3"
 */
export const buildTrackFilename = (track) => {
  const title = track.name || track.title || 'track';
  const artist = track.artist || track.channelTitle || '';
  const base = artist && !title.startsWith(artist) ? `${artist} - ${title}` : title;
  return `${sanitizeFilename(base) || 'track'}.mp3`;
};

/**
 * Эндпоинт и параметры скачивания для трека
 */
export const getDownloadRequest = (track) => {
  const source = track.source || 'yandex';
  const filename = buildTrackFilename(track);

  switch (source) {
    case 'yandex':
      if (!track.id) throw new Error('Нет ID трека Яндекс.Музыки');
      return { url: `${API_URL}/api/track/${track.id}/download`, params: {}, filename };
    case 'spotify': {
      const ref = track.id || track.url || track.externalUrl;
      if (!ref) throw new Error('Нет ID трека Spotify');
      return { url: `${API_URL}/api/spotify/download`, params: { track_id: ref }, filename };
    }
    case 'soundcloud': {
      const link = track.url || track.externalUrl;
      if (!link && !track.id) throw new Error('Нет ссылки на трек SoundCloud');
      return {
        url: `${API_URL}/api/soundcloud/download`,
        params: link ? { url: link, name: filename.replace(/\.mp3$/, '') } : { track_id: track.id },
        filename
      };
    }
    case 'youtube':
    default:
      if (!track.url) throw new Error('Нет ссылки на видео YouTube');
      return { url: `${API_URL}/api/youtube/download`, params: { url: track.url }, filename };
  }
};

/**
 * Достаёт имя файла из Content-Disposition, иначе возвращает fallback
 */
const filenameFromHeaders = (headers, fallback) => {
  const header = headers && (headers['content-disposition'] || headers['Content-Disposition']);
  if (!header) return fallback;
  const utf8 = header.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8) {
    try {
      return sanitizeFilename(decodeURIComponent(utf8[1])) || fallback;
    } catch (e) {
      return fallback;
    }
  }
  return fallback;
};

/**
 * Превращает ошибку axios (в т.ч. с blob-телом) в читаемое сообщение
 */
export const getDownloadErrorMessage = async (error) => {
  const response = error && error.response;
  if (response) {
    let data = response.data;
    if (data instanceof Blob) {
      try {
        data = JSON.parse(await data.text());
      } catch (e) {
        data = null;
      }
    }
    if (data && data.error) return data.error;
    if (response.status === 404) return 'Трек не найден (404)';
    if (response.status === 401) return 'Нужна авторизация источника (401)';
    if (response.status >= 500) return `Ошибка сервера (${response.status})`;
    return `Ошибка ${response.status}`;
  }
  if (error && error.code === 'ECONNABORTED') return 'Таймаут: трек слишком долго готовится';
  return (error && error.message) || 'Не удалось скачать трек';
};

/**
 * Скачивает трек в Blob. Бросает Error с читаемым сообщением.
 */
export const fetchTrackBlob = async (track, { timeout = 300000 } = {}) => {
  const request = getDownloadRequest(track);
  try {
    const response = await axios.get(request.url, {
      params: request.params,
      responseType: 'blob',
      timeout
    });
    if (!response.data || response.data.size === 0) {
      throw new Error('Получен пустой файл');
    }
    return {
      blob: response.data,
      filename: filenameFromHeaders(response.headers, request.filename)
    };
  } catch (error) {
    throw new Error(await getDownloadErrorMessage(error));
  }
};

/**
 * Сохраняет Blob в файл через временную ссылку
 */
export const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Chrome начинает загрузку асинхронно — не отзываем ссылку сразу
  setTimeout(() => window.URL.revokeObjectURL(url), 30000);
};

/**
 * Скачивает и сохраняет один трек
 */
export const downloadTrack = async (track) => {
  const { blob, filename } = await fetchTrackBlob(track);
  saveBlob(blob, filename);
  return filename;
};

/**
 * Скачивает список треков с ограничением параллелизма и одной повторной попыткой.
 * @returns {Promise<{done: number, failed: Array<{track, message}>}>}
 */
export const downloadTracksBatch = async (tracks, { concurrency = 3, onProgress } = {}) => {
  let nextIndex = 0;
  let finished = 0;
  const failed = [];

  const report = () => {
    if (onProgress) onProgress({ current: finished, total: tracks.length });
  };
  report();

  const worker = async () => {
    while (nextIndex < tracks.length) {
      const track = tracks[nextIndex++];
      try {
        try {
          await downloadTrack(track);
        } catch (firstError) {
          // Одна повторная попытка: источники иногда отвечают 5xx на первый запрос
          await downloadTrack(track);
        }
      } catch (error) {
        failed.push({ track, message: error.message });
      }
      finished++;
      report();
    }
  };

  await Promise.all(Array.from({ length: Math.min(concurrency, tracks.length) }, worker));
  return { done: tracks.length - failed.length, failed };
};
